import { cn } from "@/utils/cn";
import { memo } from "react";

export type ProgressBreakpoints = [number, number, number, number, number];
export type SegmentMaxPercentages = [number, number, number, number];

interface ProgressBarTrackProps {
  breakpoints: ProgressBreakpoints;
  value: number;
  segmentMaxPercentages?: SegmentMaxPercentages;
}

const DEFAULT_SEGMENT_MAX_PERCENTAGES: SegmentMaxPercentages = [30, 50, 70, 100];

export const getMarkerPositionPercent = (
  breakpoints: ProgressBreakpoints,
  value: number,
  segmentMaxPercentages: SegmentMaxPercentages,
) => {
  const [b0, b1, b2, b3, b4] = breakpoints;
  const segments: Array<[number, number, number]> = [
    [b0, b1, segmentMaxPercentages[0]],
    [b1, b2, segmentMaxPercentages[1]],
    [b2, b3, segmentMaxPercentages[2]],
    [b3, b4, segmentMaxPercentages[3]],
  ];

  if (value <= b0) {
    return 0;
  }

  if (value >= b4) {
    return 100;
  }

  for (let index = 0; index < segments.length; index += 1) {
    const [start, end, maxPercent] = segments[index];

    if (value <= end) {
      const segmentRange = end - start;
      const localProgress = segmentRange === 0 ? 0 : (value - start) / segmentRange;
      const previousMaxPercent = index === 0 ? 0 : segments[index - 1][2];
      const currentSegmentPercentRange = maxPercent - previousMaxPercent;

      return previousMaxPercent + localProgress * currentSegmentPercentRange;
    }
  }

  return 100;
};

export const ProgressBarTrack = memo(function ProgressBarTrack({
  breakpoints,
  value,
  segmentMaxPercentages = DEFAULT_SEGMENT_MAX_PERCENTAGES,
}: ProgressBarTrackProps) {
  const markerPosition = getMarkerPositionPercent(breakpoints, value, segmentMaxPercentages);

  return (
    <div className="relative mt-1 h-7">
      <div
        className={cn(
          "absolute inset-0 rounded-[30px]",
          "bg-[linear-gradient(180deg,rgba(207,213,224,0.08)_-46.25%,rgba(239,241,245,0.08)_100%)]",
          "shadow-[inset_0_4px_6px_0_rgba(0,0,0,0.06)]",
        )}
      />
      <div className={cn("absolute inset-[1px] overflow-hidden rounded-[30px]")}>
        <div
          className={cn(
            "absolute top-1/2 left-1/2 h-[133px] w-[133px] -translate-x-1/2 -translate-y-1/2 rounded-full",
            "bg-[linear-gradient(90deg,rgba(92,246,40,0)_0%,#45D35F_50%,rgba(92,246,40,0)_100%)]",
          )}
        />
      </div>
      <div className="absolute inset-0 rounded-[30px] border border-[rgba(42,10,63,0.40)]" />
      <div className="absolute inset-y-0 right-1 left-1">
        <div
          data-testid="weekly-deficit-marker"
          className="absolute top-1/2 h-[22px] w-1 -translate-x-1/2 -translate-y-1/2 bg-[linear-gradient(180deg,#FFF_0%,#999_343.75%)] shadow-[0_2px_6px_0_rgba(0,0,0,0.25)]"
          style={{ left: `${markerPosition}%` }}
        />
      </div>
    </div>
  );
});
